import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { TapIndicator } from "../TapIndicator";
import { useNarrative } from "../../hooks/useNarrative";
import { MuteToggle } from "../MuteToggle";

interface NarrativeSectionProps {
  onNarrativeClick?: () => void;
}

const NarrativeSection: React.FC<NarrativeSectionProps> = ({ onNarrativeClick }) => {
  const { currentTexts, advanceNarrative, currentDelay, currentKey } =
    useNarrative();
  const [isReady, setIsReady] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [textIndex, setTextIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Detect iOS devices
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  useEffect(() => {
    // Only apply delay when showing the last text in the array
    const isLastText = textIndex === currentTexts.length - 1;
    
    if (!isLastText) {
      setIsReady(true); // No delay for intermediate texts
    } else {
      setIsReady(false);
      const timer = setTimeout(() => {
        setIsReady(true);
      }, currentDelay);

      return () => clearTimeout(timer);
    }
  }, [textIndex, currentTexts.length, currentDelay]);

  useEffect(() => {
    if (!isIOS && videoRef.current && currentKey) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch((err) => {
        console.log("Video play failed:", err);
      });
    }
    // Reset text index when moving to a new narrative key
    setTextIndex(0);
  }, [currentKey, isIOS]);

  const handleClick = () => {
    onNarrativeClick?.();
    
    if (textIndex < currentTexts.length - 1) {
      // More texts in array - advance immediately without delay check
      setTextIndex(textIndex + 1);
    } else if (isReady) {
      // Last text and delay passed - advance to next narrative key
      advanceNarrative();
    }
  };

  return (
    <div className="relative px-2 sm:p-1 bg-gradient-to-t from-gray-900 to-black border-t border-cyan-900/50">
      <div className="w-full h-full sm:max-w-4xl sm:mx-auto flex flex-col relative">
        {/* Mute button - above on mobile, to the right on desktop */}
        <div className="sm:hidden">
          <MuteToggle />
        </div>
        <div className="relative sm:flex sm:items-start sm:gap-2">
          <div
            className={`relative flex flex-1 m-0 sm:mb-0 sm:mt-1 md:mt-1 bg-black/50 rounded-none sm:rounded-lg border-0 sm:border border-cyan-500/20 transition-all ${
              isReady
                ? "cursor-pointer hover:bg-black/60 hover:border-cyan-500/30 active:scale-[0.99]"
                : "cursor-default"
            } ${!videoLoaded ? "opacity-0 pointer-events-none" : "opacity-100"}`}
            onClick={handleClick}
            style={{ transition: "opacity 0.3s ease-in-out" }}
          >
          {/* Avatar Box - Fixed size square */}
          <div className="relative bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border-r border-cyan-500/20 overflow-visible flex-shrink-0">
            {/* This div maintains square aspect ratio */}
            <div className="w-[5rem] h-[5rem] sm:w-[7rem] sm:h-[7rem] md:w-[9rem] md:h-[9rem] flex items-center justify-center relative">
              {isIOS ? (
                <img
                  src="/catbuto2.png"
                  alt="Cat Buto"
                  onLoad={() => setVideoLoaded(true)}
                  onError={() => setVideoLoaded(true)}
                  className="absolute inset-0 w-full h-full object-contain scale-[130%] z-10 -translate-y-[12.5%]"
                />
              ) : (
                <video
                  ref={videoRef}
                  src="/catbuto2_yap.webm"
                  muted
                  playsInline
                  onLoadedData={() => {
                    console.log("Video loaded!");
                    setVideoLoaded(true);
                  }}
                  onError={(e) => {
                    console.error("Video error:", e);
                    setVideoLoaded(true); // Show section anyway on error
                  }}
                  className="absolute inset-0 w-full h-full object-contain scale-[130%] z-10 -translate-y-[12.5%]"
                />
              )}
            </div>
          </div>

          {/* Narrative Text */}
          <div className="flex-1 pl-4 pr-2 py-2 sm:p-6 md:p-8 h-[5rem] sm:h-[7rem] md:h-[9rem] flex items-center overflow-hidden">
            {currentTexts[textIndex] && (
              <motion.p
                key={`${currentTexts[textIndex]}-${textIndex}`}
                className="text-cyan-100 font-mono text-xs sm:text-base md:text-lg leading-relaxed overflow-y-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
              >
                {currentTexts[textIndex]}
              </motion.p>
            )}
          </div>

            <motion.div
              className="absolute bottom-2 right-4 -rotate-90 pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: isReady ? 1 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <TapIndicator onClick={() => {}} position="inline" />
            </motion.div>
          </div>
          {/* Mute button on desktop - to the right of narrative box */}
          <div className="hidden sm:block sm:mt-1 md:mt-1">
            <MuteToggle />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NarrativeSection;